import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import BrandSymbol from '../ui/BrandSymbol';
import { useSessionPersistence } from '../../hooks/useSessionPersistence';
import { apiService } from '../../utils/api';
import { ResponsiveContainer, ResponsiveContent, ResponsiveFooter } from '../ui/ResponsiveLayout';

interface ExecutorInvitation {
  id: string;
  email: string;
  name?: string;
  status: 'pending' | 'accepted';
  invitedAt: Date;
}

const Frame163: React.FC = () => {
  const navigate = useNavigate();
  const { session, completeTask } = useSessionPersistence();
  const [personalizedMessage, setPersonalizedMessage] = useState('');
  const [selectedExecutors, setSelectedExecutors] = useState<ExecutorInvitation[]>([]);
  const [isSendingEmails, setIsSendingEmails] = useState(false);

  // Load saved executor data on component mount
  useEffect(() => {
    console.log('Frame163: Loading executor data from session');
    if (session.taskProgress?.executors) {
      const savedData = session.taskProgress.executors;
      console.log('Frame163: Saved data structure:', savedData);
      
      // The data is stored in the answers object by updateTaskProgress
      if (savedData.answers?.selectedExecutors && savedData.answers?.invitedExecutors) {
        const selectedIds = savedData.answers.selectedExecutors;
        const allExecutors = savedData.answers.invitedExecutors;
        console.log('Frame163: Selected IDs:', selectedIds);
        console.log('Frame163: All executors:', allExecutors);
        
        // Fix date serialization issue - convert string dates back to Date objects
        const fixedExecutors = allExecutors.map((executor: unknown) => ({
          ...executor,
          invitedAt: executor.invitedAt ? new Date(executor.invitedAt) : new Date()
        }));
        
        const selected = fixedExecutors.filter(executor => selectedIds.includes(executor.id));
        console.log('Frame163: Selected executors:', selected);
        setSelectedExecutors(selected);
      }
    }
  }, [session.taskProgress?.executors]);

  const handleBack = () => {
    navigate('/frame-158');
  };

  const handleNext = async () => {
    if (selectedExecutors.length === 0) {
      alert('Please select at least one executor before proceeding.');
      return;
    }

    setIsSendingEmails(true);
    
    try {
      console.log('Frame163: Queueing executor notification emails');
      
      // Queue the executor notification emails
      const success = await apiService.queueExecutorNotifications(
        selectedExecutors,
        personalizedMessage || getDefaultMessage()
      );
      
      if (success) {
        console.log('Frame163: Successfully queued executor notifications');
        
        // Mark the executor task and parent legal_essentials task as completed
        console.log('Frame163: Marking executor and legal_essentials tasks as complete');
        await completeTask('executors');
        await completeTask('legal_essentials');
        console.log('Frame163: Tasks marked as complete');
        
        // Navigate to summary page
        navigate('/frame-165');
      } else {
        console.error('Frame163: Failed to queue executor notifications');
        alert('There was an issue sending the executor notifications. Please try again.');
      }
    } catch (error) {
      console.error('Frame163: Error queueing executor notifications:', error);
      alert('There was an issue sending the executor notifications. Please try again.');
    } finally {
      setIsSendingEmails(false);
    }
  };

  const getDefaultMessage = () => {
    const executorNames = selectedExecutors.map(executor => 
      executor.name || executor.email.split('@')[0]
    ).join(', ');
    
    return `Dear ${executorNames},

I hope this message finds you well. I wanted to personally reach out to let you know that I have selected you to serve as an executor of my estate.

This is an important responsibility that I don't take lightly, and I chose you because I trust you completely to handle my affairs with care and integrity when the time comes.

End of an Era will be sending you an email with all the details about your role and responsibilities. You'll also receive a secure link to register an account where you can formally accept this position and access important resources.

Please take the time to review the information carefully. If you have any questions or concerns, don't hesitate to reach out to me directly.

Thank you for considering this important role in my life.

Best regards`;
  };

  const getExecutorDisplayNames = () => {
    if (selectedExecutors.length === 0) return 'No executors selected';
    
    const names = selectedExecutors.map(executor => executor.name || executor.email);
    if (names.length === 1) return names[0];
    if (names.length === 2) return `${names[0]} & ${names[1]}`;
    return `${names[0]} & ${names[1]}...`;
  };

  const handleUseTemplate = () => {
    const executorNames = selectedExecutors.map(executor => 
      executor.name || executor.email.split('@')[0]
    ).join(', ');
    
    const templateMessage = `Dear ${executorNames},

I hope this message finds you well. I wanted to personally reach out to let you know that I have selected you to serve as an executor of my estate.

This is an important responsibility that I don't take lightly, and I chose you because I trust you completely to handle my affairs with care and integrity when the time comes.

End of an Era will be sending you an email with all the details about your role and responsibilities. You'll also receive a secure link to register an account where you can formally accept this position and access important resources.

Please take the time to review the information carefully. If you have any questions or concerns, don't hesitate to reach out to me directly.

Thank you for considering this important role in my life.

Best regards`;
    
    setPersonalizedMessage(templateMessage);
  };

  return (
    <ResponsiveContainer background="stone">
      {/* Centered Brand Logo */}
      <div className="absolute top-8 left-1/2 transform -translate-x-1/2 z-10">
        <BrandSymbol size="3xl" showText variant="dark" />
            </div>

      <ResponsiveContent className="pt-32">
        {/* Header with title */}
        <div className="max-w-4xl mx-auto mb-8">
          <h1 className="text-cyan-900 text-2xl sm:text-3xl font-semibold font-['Test_Domaine_Display'] leading-tight">
            Notify your executor(s)
          </h1>
          </div>
          
        {/* Main Content */}
        <div className="max-w-4xl mx-auto">
          {/* Description */}
          <div className="mb-8">
            <p className="text-stone-800 text-lg font-medium font-['Work_Sans'] leading-relaxed">
              Thank you. End of an Era will email the following people to let them know they've been elected as executors, 
              along with all the resources they'll need to understand the role.
            </p>
          </div>
          
          {/* Executor Cards */}
          <div className="mb-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {selectedExecutors.map((executor) => (
                <div 
                  key={executor.id} 
                  className="h-12 bg-slate-100 rounded-[40px] border border-slate-400 flex items-center justify-start px-3 min-w-0"
                >
                  {/* Avatar circle */}
                  <div className="w-9 h-9 bg-white rounded-full flex-shrink-0"></div>
                  
                  {/* User icon */}
                  <div className="ml-2 flex-shrink-0">
                    <svg className="w-3 h-3.5 text-cyan-700" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                    </svg>
                          </div>
                          
                  {/* Name and email container */}
                  <div className="ml-3 flex-1 min-w-0 text-left">
                    {/* Name */}
                    <div className="text-stone-800 text-base font-semibold font-['Work_Sans'] truncate text-left">
                      {executor.name || executor.email.split('@')[0]}
                            </div>
                    {/* Email */}
                    <div className="text-cyan-700 text-sm font-normal font-['Work_Sans'] truncate text-left">
                      {executor.email}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

          {/* Personalized Message Section */}
          <div className="mb-4">
            <h3 className="text-stone-800 text-lg font-medium font-['Work_Sans'] mb-4">
              Add a personalized message
            </h3>
            
            {/* Message textarea */}
            <div className="w-full bg-white rounded border border-neutral-200 mb-4">
              <textarea
                value={personalizedMessage}
                onChange={(e) => setPersonalizedMessage(e.target.value)}
                placeholder={getExecutorDisplayNames()}
                className="w-full h-36 p-4 text-stone-500 text-base font-normal font-serif leading-relaxed resize-none border-none outline-none bg-transparent"
                rows={4}
              />
            </div>
            
            {/* Use Template Button */}
            <button
              onClick={handleUseTemplate}
              className="px-4 py-2 bg-cyan-700 text-white rounded-md hover:bg-cyan-800 transition-colors text-sm font-medium"
            >
              Use our Template
            </button>
          </div>
        </div>
      </ResponsiveContent>

      {/* Responsive Footer */}
      <ResponsiveFooter
        title="Executor Selection"
        subtitle="Approximately 15 minutes"
        progressPercent={20}
        onBack={handleBack}
        onNext={handleNext}
        nextDisabled={isSendingEmails}
        backText="← Back"
        nextText={isSendingEmails ? 'Sending...' : 'Next →'}
      />
    </ResponsiveContainer>
  );
};

export default Frame163; 